/**
 * 입고작업 2.0(GL17) API 타입.
 *
 * 스캔 — moms-be `GlobalInboundScanController.kt`(`POST /api/v1/global/inbound-works/scan`)의 요청·응답 DTO 그대로.
 * 첨부 완료 — moms-be LOGISPRD-16057 `GlobalInboundAttachmentController.kt`(PR #5351)의 요청·응답 DTO 그대로.
 */
import type { CommonFileResourceType } from '@/services/commonFile/types'

/** BE `GlobalInboundScanUsecase.ScanCommandType`. 생략(null)이면 일반 스캔(NORMAL). */
export type ScanCommandType = 'NORMAL' | 'QUANTITY_MISMATCH' | 'VARIANT' | 'RESET' | 'REPRINT'

/** BE `GlobalInboundScanUsecase.ScanResultType`. */
export type ScanResultType =
  | 'MATCHED'
  | 'NOT_FOUND'
  | 'MULTI_CONSOLIDATION'
  | 'DUPLICATED'
  | 'QUANTITY_MISMATCH'
  | 'VARIANT'
  | 'RESET'
  | 'REPRINT'
  | 'TO_BE_BLOCKED'

/** BE `GlobalInboundScanRequest`. 송장 검증(공백 불가·7자 이상·역슬래시/₩ 금지)은 서버도 한다. */
export interface InboundScanReq {
  invoiceNumber: string
  command: ScanCommandType | null
}

/** BE `GlobalInboundScanResponse.Row`. */
export interface InboundScanRowRes {
  shipperCode: string
  /** 입고상태 — 1.0 ORD_STATUS 원문(입고대기/입고요청) */
  orderStatus: string
  invoiceNumber: string
  consolidationNumber: string | null
  serviceType: string | null
  storageZone: string | null
  dangerous: boolean
  dangerousQuantity: number
  normalQuantity: number
  totalQuantity: number
  variant: boolean
  outboundRequestDate: string | null
}

/** BE `GlobalInboundScanResponse.OrderLabel`. */
export interface InboundScanOrderRes {
  shipperOrderNumber: string | null
  shipperOrderOptionNumber: number | null
  deliveryCountry: string | null
}

/** BE `GlobalInboundScanResponse`. 미매칭은 HTTP 200 + succeeded=false 다. */
export interface InboundScanRes {
  scanResult: ScanResultType
  succeeded: boolean
  message: string | null
  printQuantity: number
  rows: InboundScanRowRes[]
  orders: InboundScanOrderRes[]
}

/** BE `GlobalInboundAttachmentFileRequest` — key 는 공통 준비 API(MUSINSAGLOBAL)가 준 값. */
export interface InboundAttachmentFileReq {
  key: string
  resourceType: CommonFileResourceType
}

/**
 * BE `GlobalInboundAttachmentRequest`. originalInvoiceNumber = 스캔 API 에 보낸 송장(1.0 ORG_TRS_INVOICE).
 * files 는 1~10건(`INBOUND_ATTACHMENT_MAX`)이다.
 */
export interface InboundAttachmentSaveReq {
  originalInvoiceNumber: string
  files: InboundAttachmentFileReq[]
}

/**
 * BE `GlobalInboundAttachmentResponse`. historyFound=false 면 아무것도 저장하지 않았다(1.0 과 같이 성공 응답).
 * fileId = 저장 뒤 FILE_ID 원문 · truncated = FILE_ID 20자를 넘어 앞 20자만 저장했다(1.0 과 같은 절단 — 일부 첨부가 이력에 안 붙음).
 */
export interface InboundAttachmentSaveRes {
  historyFound: boolean
  fileId: string | null
  fileInfoIds: number[]
  truncated: boolean
}

/**
 * 화면·라벨이 읽는 행 — 1.0 GL09_HEAD_DATA 키 그대로(1.0 grid.js `toGl09ScanResult`). 라벨 분기를 1.0 과 같은 식으로 옮기려고 키를 바꾸지 않는다.
 * boolean 은 1.0 표기대로 'Y'/'N'. PRINT_QTY 는 응답 최상위 printQuantity 를 모든 행에 싣는다(1.0 과 같다 — 라벨은 첫 행만 읽는다).
 */
export interface InboundScanRow {
  SPR_NM: string
  ORD_STATUS: string
  INB_TRS_INVOICE: string
  ORD_CONSOL_NO: string
  SVC_TYPE: string
  INV_TYPE: string
  DANGER: 'Y' | 'N'
  DANGER_QTY: number
  NORMAL_QTY: number
  TOTAL_QTY: number
  IS_VARIANT: 'Y' | 'N'
  OUB_PDATE: string
  PRINT_QTY: number
}

/** 라벨 주문번호 블록(1.0 `orderList`). */
export interface InboundOrderLabel {
  SPR_ORD_NO: string
  SPR_ORD_OPT_NO: string
  TRS_COUNTRY: string
}

/** 1.0 grid.js `toGl09ScanResult` 의 rtnData 변환. null 은 빈 문자열(1.0 라벨이 `.includes` 를 부른다). */
export const toInboundScanRows = (res: InboundScanRes): InboundScanRow[] =>
  res.rows.map((row) => ({
    SPR_NM: row.shipperCode,
    ORD_STATUS: row.orderStatus,
    INB_TRS_INVOICE: row.invoiceNumber,
    ORD_CONSOL_NO: row.consolidationNumber ?? '',
    SVC_TYPE: row.serviceType ?? '',
    INV_TYPE: row.storageZone ?? '',
    DANGER: row.dangerous ? 'Y' : 'N',
    DANGER_QTY: row.dangerousQuantity,
    NORMAL_QTY: row.normalQuantity,
    TOTAL_QTY: row.totalQuantity,
    IS_VARIANT: row.variant ? 'Y' : 'N',
    OUB_PDATE: row.outboundRequestDate ?? '',
    PRINT_QTY: res.printQuantity,
  }))

/** 1.0 grid.js `toGl09ScanResult` 의 orderList 변환. */
export const toInboundOrderLabels = (res: InboundScanRes): InboundOrderLabel[] =>
  res.orders.map((order) => ({
    SPR_ORD_NO: order.shipperOrderNumber ?? '',
    SPR_ORD_OPT_NO: order.shipperOrderOptionNumber == null ? '' : String(order.shipperOrderOptionNumber),
    TRS_COUNTRY: order.deliveryCountry ?? '',
  }))
